import React from "react";
import { Link, useParams } from "react-router-dom";

import {
  FaCheckCircle,
  FaTimesCircle,
  FaQuestionCircle,
} from "react-icons/fa";

import {
  useGetEnglishQuizAttemptDetailsAdminQuery,
} from "../../../slices/englishQuizAttemptApiSlice";


const EnglishQuizAttemptDetailsAdminScreen = () => {
  const { attemptId } = useParams();

  const {
    data: attempt,
    isLoading,
    error,
  } =
    useGetEnglishQuizAttemptDetailsAdminQuery(
      attemptId,
    );


  // ======================================================
  // Loading
  // ======================================================

  if (isLoading) {
    return (
      <div className="p-10 text-center">
        Loading attempt details...
      </div>
    );
  }


  // ======================================================
  // Error
  // ======================================================

  if (error || !attempt) {
    return (
      <div className="p-10 text-center text-red-600">
        Unable to load attempt details.
      </div>
    );
  }


  const student = attempt.student;
  const quiz = attempt.quiz;


  return (
    <div className="min-h-screen bg-gray-50 p-6">

      <div className="max-w-5xl mx-auto">

        {/* ==================================================
            Back
        ================================================== */}

        <Link
          to={`/admin/english-quiz-attempts/quiz/${quiz?._id}`}
          className="
            inline-block
            mb-5
            text-blue-600
          "
        >
          ← Back to Student Attempts
        </Link>


        {/* ==================================================
            Header
        ================================================== */}

        <div
          className="
            rounded-2xl
            bg-white
            p-6
            shadow
          "
        >

          <h1 className="text-2xl font-bold text-gray-800">
            Attempt Details
          </h1>

          <div className="mt-4">

            <p className="text-lg font-semibold">
              {student?.name || "Unknown Student"}
            </p>

            {student?.studentId && (
              <p className="text-sm text-gray-500">
                Student ID: {student.studentId}
              </p>
            )}

            {student?.phone && (
              <p className="text-sm text-gray-500">
                Phone: {student.phone}
              </p>
            )}

          </div>


          <div className="mt-4">

            <p className="font-semibold">
              {quiz?.name || "English Quiz"}
            </p>

            <p className="text-sm text-gray-500">
              Attempt #{attempt.attemptNumber || 1}
            </p>

          </div>


          {/* ==================================================
              Summary
          ================================================== */}

          <div
            className="
              mt-6
              grid
              grid-cols-2
              md:grid-cols-5
              gap-4
            "
          >

            <div className="rounded-xl bg-blue-50 p-4">

              <p className="text-sm text-gray-500">
                Questions
              </p>

              <p className="mt-1 text-xl font-bold">
                {attempt.totalQuestions ||
                  attempt.questions?.length ||
                  0}
              </p>

            </div>


            <div className="rounded-xl bg-green-50 p-4">

              <p className="text-sm text-gray-500">
                Correct
              </p>

              <p className="mt-1 text-xl font-bold text-green-700">
                {attempt.correctAnswers || 0}
              </p>

            </div>


            <div className="rounded-xl bg-red-50 p-4">

              <p className="text-sm text-gray-500">
                Wrong
              </p>

              <p className="mt-1 text-xl font-bold text-red-700">
                {attempt.wrongAnswers || 0}
              </p>

            </div>


            <div className="rounded-xl bg-yellow-50 p-4">

              <p className="text-sm text-gray-500">
                Unanswered
              </p>

              <p className="mt-1 text-xl font-bold text-yellow-700">
                {attempt.unansweredQuestions || 0}
              </p>

            </div>


            <div className="rounded-xl bg-purple-50 p-4">

              <p className="text-sm text-gray-500">
                Score
              </p>

              <p className="mt-1 text-xl font-bold text-purple-700">
                {attempt.obtainedMarks ?? 0}
                {" / "}
                {attempt.totalMarks ?? 0}
              </p>

              <p className="text-sm text-gray-500">
                {attempt.percentage ?? 0}%
              </p>

            </div>

          </div>


          {/* ==================================================
              Result
          ================================================== */}

          <div className="mt-6">

            {attempt.result === "pass" ? (
              <div
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  bg-green-100
                  px-4
                  py-2
                  font-semibold
                  text-green-700
                "
              >
                <FaCheckCircle />
                Passed
              </div>
            ) : (
              <div
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  bg-red-100
                  px-4
                  py-2
                  font-semibold
                  text-red-700
                "
              >
                <FaTimesCircle />
                Failed
              </div>
            )}

          </div>

        </div>


        {/* ==================================================
            Questions
        ================================================== */}

        <div className="mt-6 space-y-5">

          {(attempt.questions || []).map(
            (question, index) => {

              const answer =
                (attempt.answers || []).find(
                  (item) =>
                    item.question?.toString() ===
                    question._id?.toString(),
                );


              const selectedAnswer =
                answer?.selectedAnswer;


              const isCorrect =
                answer?.isCorrect === true;


              const unanswered =
                selectedAnswer === null ||
                selectedAnswer === undefined ||
                selectedAnswer === "" ||
                (
                  Array.isArray(
                    selectedAnswer,
                  ) &&
                  selectedAnswer.length === 0
                );


              return (
                <div
                  key={question._id}
                  className="
                    rounded-2xl
                    bg-white
                    p-6
                    shadow
                  "
                >

                  {/* ==================================================
                      Question Header
                  ================================================== */}

                  <div className="flex gap-3">

                    <div
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-blue-100
                        font-bold
                        text-blue-700
                      "
                    >
                      {index + 1}
                    </div>

                    <div className="flex-1">

                      <p
                        className="
                          text-lg
                          font-semibold
                          text-gray-800
                          whitespace-pre-line
                        "
                      >
                        {question.questionText}
                      </p>


                      {/* ==================================================
                          Highlighted Text
                      ================================================== */}

                      {question.highlightedText && (
                        <div
                          className="
                            mt-3
                            rounded-lg
                            bg-yellow-50
                            p-3
                            font-semibold
                          "
                        >
                          {question.highlightedText}
                        </div>
                      )}


                      {/* ==================================================
                          Statements
                      ================================================== */}

                      {question.statements?.length > 0 && (
                        <div className="mt-4 space-y-2">

                          {question.statements.map(
                            (statement) => (
                              <div
                                key={statement.key}
                                className="
                                  rounded-lg
                                  bg-gray-50
                                  p-3
                                "
                              >
                                <span className="font-semibold">
                                  {statement.key}.
                                </span>{" "}
                                {statement.text}
                              </div>
                            ),
                          )}

                        </div>
                      )}


                      {/* ==================================================
                          Image
                      ================================================== */}

                      {question.imageUrl && (
                        <img
                          src={question.imageUrl}
                          alt="Question"
                          className="
                            mt-4
                            max-w-full
                            rounded-xl
                          "
                        />
                      )}


                      {/* ==================================================
                          Options
                      ================================================== */}

                      {question.options?.length > 0 && (
                        <div className="mt-4 space-y-2">

                          {question.options.map(
                            (option) => {

                              const isSelected =
                                Array.isArray(
                                  selectedAnswer,
                                )
                                  ? selectedAnswer.includes(
                                      option.key,
                                    )
                                  : selectedAnswer ===
                                    option.key;


                              const isCorrectOption =
                                Array.isArray(
                                  question.correctAnswer,
                                )
                                  ? question.correctAnswer.includes(
                                      option.key,
                                    )
                                  : question.correctAnswer ===
                                    option.key;


                              let optionClass =
                                "border-gray-200";


                              if (
                                isCorrectOption
                              ) {
                                optionClass =
                                  "border-green-400 bg-green-50";
                              } else if (
                                isSelected
                              ) {
                                optionClass =
                                  "border-red-400 bg-red-50";
                              }


                              return (
                                <div
                                  key={option.key}
                                  className={`
                                    rounded-xl
                                    border
                                    p-3
                                    ${optionClass}
                                  `}
                                >

                                  <span className="font-semibold">
                                    {option.key}.
                                  </span>{" "}

                                  {option.text}


                                  {isCorrectOption && (
                                    <span
                                      className="
                                        ml-2
                                        text-sm
                                        font-semibold
                                        text-green-600
                                      "
                                    >
                                      Correct Answer
                                    </span>
                                  )}


                                  {isSelected &&
                                    !isCorrectOption && (
                                      <span
                                        className="
                                          ml-2
                                          text-sm
                                          font-semibold
                                          text-red-600
                                        "
                                      >
                                        Student Answer
                                      </span>
                                    )}

                                </div>
                              );
                            },
                          )}

                        </div>
                      )}


                      {/* ==================================================
                          Answer Status
                      ================================================== */}

                      <div className="mt-5">

                        {unanswered ? (
                          <div
                            className="
                              flex
                              items-center
                              gap-2
                              text-yellow-600
                            "
                          >
                            <FaQuestionCircle />

                            <span className="font-semibold">
                              Not Answered
                            </span>
                          </div>
                        ) : isCorrect ? (
                          <div
                            className="
                              flex
                              items-center
                              gap-2
                              text-green-600
                            "
                          >
                            <FaCheckCircle />

                            <span className="font-semibold">
                              Correct
                            </span>
                          </div>
                        ) : (
                          <div
                            className="
                              flex
                              items-center
                              gap-2
                              text-red-600
                            "
                          >
                            <FaTimesCircle />

                            <span className="font-semibold">
                              Incorrect
                            </span>
                          </div>
                        )}

                      </div>


                      {/* ==================================================
                          Marks
                      ================================================== */}

                      <div className="mt-3 text-sm text-gray-500">

                        Marks:{" "}
                        <span className="font-semibold">
                          {question.marks ?? 0}
                        </span>

                        {" | "}

                        Negative Marks:{" "}
                        <span className="font-semibold">
                          {question.negativeMarks ?? 0}
                        </span>

                      </div>


                      {/* ==================================================
                          Explanation
                      ================================================== */}

                      {question.explanation && (
                        <div
                          className="
                            mt-4
                            rounded-xl
                            bg-blue-50
                            p-4
                          "
                        >

                          <p className="font-semibold text-blue-800">
                            Explanation
                          </p>

                          <p
                            className="
                              mt-1
                              whitespace-pre-line
                              text-gray-700
                            "
                          >
                            {question.explanation}
                          </p>

                        </div>
                      )}

                    </div>

                  </div>

                </div>
              );
            },
          )}

        </div>

      </div>

    </div>
  );
};


export default EnglishQuizAttemptDetailsAdminScreen;